import type { RoleRef } from '@dawn/feature-config';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/queries/query-keys';
import { isAlreadyChangedError } from '@/lib/admin-action-error';
import { adminsApi } from '@/lib/api';

/**
 * Replace a pending or expired invitation's roles (PUT /admins/invitations/:id/roles,
 * TDEV-1213). On success, and on a 404 (revoked) or 409 (accepted) that means the
 * roster moved, we refetch the roster so the table reflects server truth. The
 * refetch is awaited: the dialog closes on the save promise and reopens from the
 * row the table holds, so settling before the roster is fresh would reopen on
 * old roles.
 */
export function useUpdateInvitationRoles() {
  const queryClient = useQueryClient();
  const refreshRoster = () => queryClient.invalidateQueries({ queryKey: queryKeys.admins.roster });
  return useMutation<
    { id: string; roles: RoleRef[] },
    Error,
    { invitationId: string; roleIds: string[] }
  >({
    mutationFn: ({ invitationId, roleIds }) =>
      adminsApi.updateInvitationRoles(invitationId, roleIds),
    onSuccess: refreshRoster,
    onError: (error) => (isAlreadyChangedError(error) ? refreshRoster() : undefined),
  });
}
